import { calculateVendorShippingMultipliers, recalculateItemClientPrice } from "./pricingUtils";
import { openDocumentPrintWindow } from "./pdfExportUtils";

/**
 * Formats and triggers print/PDF export for an order invoice.
 */
export function exportOrderToPdf(po, options = {}) {
  if (!po) return;

  const { logoUrl, companyName, paySchedule } = options;

  const fmt = (val) => {
    const num = Number(val || 0);
    return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getPoMarkupRate = () => {
    if (po.markupRate !== undefined && po.markupRate !== null) {
      return parseFloat(po.markupRate);
    }
    const saved = localStorage.getItem("global_markup_rate");
    return saved !== null ? parseFloat(saved) : 15;
  };

  const isNonCc = po.paymentMethod && po.paymentMethod.trim() !== "" && po.paymentMethod.toLowerCase() !== "credit card";
  const nonCcDisc = (() => {
    const s = localStorage.getItem("global_non_cc_discount");
    return s !== null ? parseFloat(s) : 3;
  })();
  const effectiveMarkup = isNonCc ? Math.max(0, getPoMarkupRate() - nonCcDisc) : getPoMarkupRate();

  const multipliers = calculateVendorShippingMultipliers(po.items);
  const items = (po.items || []).map((item, idx) => {
    const qty = Number(item.quantity || 1);
    const finalLineTotal = (item.unitPrice !== undefined && item.unitPrice !== null && item.unitPrice !== "")
      ? Number(item.unitPrice)
      : recalculateItemClientPrice(item, effectiveMarkup, multipliers);
    const unitPrice = qty > 0 ? (finalLineTotal / qty) : finalLineTotal;

    return {
      index: idx + 1,
      description: item.description || "Line Item",
      quantity: qty,
      unitPrice: unitPrice,
      total: finalLineTotal
    };
  });

  const subtotal = items.reduce((sum, item) => sum + item.total, 0);
  const totalAmount = Math.round(subtotal * 100) / 100;
  const paidAmount = Number(po.paidAmount || 0);
  const balanceDue = Math.max(0, totalAmount - paidAmount);

  const styles = `
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      color: #1e293b;
      background: #ffffff;
      padding: 40px;
      font-size: 13px;
      line-height: 1.5;
    }
    .invoice-container {
      max-width: 800px;
      margin: 0 auto;
    }
    .header-row {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      border-bottom: 2px solid #0284c7;
      padding-bottom: 20px;
      margin-bottom: 24px;
    }
    .brand-section {
      display: flex;
      align-items: center;
      gap: 16px;
    }
    .logo-img {
      width: 60px;
      height: 60px;
      border-radius: 8px;
      object-fit: cover;
    }
    .company-title {
      font-size: 22px;
      font-weight: 800;
      color: #0f172a;
      letter-spacing: -0.5px;
    }
    .company-sub {
      font-size: 12px;
      color: #64748b;
    }
    .invoice-badge-block {
      text-align: right;
    }
    .doc-type {
      font-size: 24px;
      font-weight: 900;
      color: #0284c7;
      letter-spacing: 0.5px;
      text-transform: uppercase;
    }
    .po-num {
      font-size: 14px;
      font-weight: 700;
      color: #334155;
      margin-top: 4px;
    }
    .meta-grid {
      display: grid;
      grid-template-columns: 1.2fr 1fr;
      gap: 24px;
      margin-bottom: 28px;
    }
    .card-box {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      padding: 14px 18px;
    }
    .card-label {
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #0284c7;
      margin-bottom: 6px;
    }
    .client-name {
      font-size: 15px;
      font-weight: 700;
      color: #0f172a;
      margin-bottom: 4px;
    }
    .meta-line {
      display: flex;
      justify-content: space-between;
      padding: 3px 0;
      font-size: 12px;
    }
    .meta-line .lbl { color: #64748b; font-weight: 500; }
    .meta-line .val { color: #0f172a; font-weight: 600; }
    
    table.items-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 24px;
    }
    table.items-table th {
      background-color: #0f172a;
      color: #ffffff;
      text-align: left;
      padding: 10px 12px;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      font-weight: 600;
    }
    table.items-table th.text-right { text-align: right; }
    table.items-table th.text-center { text-align: center; }
    table.items-table td {
      padding: 12px;
      border-bottom: 1px solid #e2e8f0;
      color: #334155;
      font-size: 12.5px;
    }
    table.items-table tr:nth-child(even) td {
      background-color: #f8fafc;
    }
    .text-right { text-align: right; }
    .text-center { text-align: center; }

    .summary-section {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 28px;
    }
    .totals-box {
      width: 280px;
      background: #f8fafc;
      border: 1px solid #cbd5e1;
      border-radius: 6px;
      padding: 14px 18px;
    }
    .totals-row {
      display: flex;
      justify-content: space-between;
      padding: 5px 0;
      font-size: 12.5px;
      color: #475569;
    }
    .totals-row.grand-total {
      border-top: 2px solid #0284c7;
      margin-top: 8px;
      padding-top: 8px;
      font-size: 15px;
      font-weight: 800;
      color: #0f172a;
    }
    .totals-row.balance-due {
      font-weight: 700;
      color: ${balanceDue > 0 ? '#dc2626' : '#16a34a'};
      font-size: 13.5px;
    }
    .footer-notes {
      border-top: 1px solid #e2e8f0;
      padding-top: 16px;
      font-size: 11px;
      color: #64748b;
      text-align: center;
    }
    .print-actions {
      margin-bottom: 20px;
      text-align: right;
    }
    .print-btn {
      background: #0284c7;
      color: #ffffff;
      border: none;
      padding: 8px 18px;
      border-radius: 6px;
      font-weight: 600;
      font-size: 13px;
      cursor: pointer;
    }
    .print-btn:hover {
      background: #0369a1;
    }

    @media print {
      body { padding: 0; }
      .print-actions { display: none; }
      @page { size: auto; margin: 12mm; }
    }
  `;

  const bodyHtml = `
  <div class="invoice-container">

    <div class="header-row">
      <div class="brand-section">
        ${logoUrl ? `<img src="${logoUrl}" class="logo-img" alt="Logo" />` : ""}
        <div>
          <div class="company-title">${companyName || "Jimini Logistics"}</div>
          <div class="company-sub">Enterprise Logistics & Procurement Services</div>
        </div>
      </div>
      <div class="invoice-badge-block">
        <div class="doc-type">Invoice</div>
        <div class="po-num"># ${po.poNumber || "N/A"}</div>
      </div>
    </div>

    <div class="meta-grid">
      <div class="card-box">
        <div class="card-label">Billed To</div>
        <div class="client-name">${po.clientName || "Valued Client"}</div>
        ${po.customerPoNumber ? `<div style="font-size: 12px; color: #475569; margin-bottom: 4px;">Cust PO: <strong>${po.customerPoNumber}</strong></div>` : ""}
        ${po.shipToAddress ? `<div style="font-size: 11.5px; color: #64748b; line-height: 1.4;">${po.shipToAddress}${po.shipToAddress2 ? `, ${po.shipToAddress2}` : ""}<br/>${po.shipToCity || ""}, ${po.shipToState || ""} ${po.shipToZip || ""}</div>` : ""}
      </div>

      <div class="card-box">
        <div class="card-label">Invoice Details</div>
        <div class="meta-line">
          <span class="lbl">Invoice Date:</span>
          <span class="val">${po.invoiceDate || po.date || new Date().toISOString().split("T")[0]}</span>
        </div>
        ${po.dueDate ? `
        <div class="meta-line">
          <span class="lbl">Payment Due:</span>
          <span class="val">${po.dueDate}</span>
        </div>` : ""}
        <div class="meta-line">
          <span class="lbl">Payment Terms:</span>
          <span class="val">${paySchedule || "Net 30"}</span>
        </div>
        <div class="meta-line">
          <span class="lbl">Payment Status:</span>
          <span class="val" style="color: ${po.paidStatus === "Paid" ? '#16a34a' : '#d97706'}; font-weight: 700;">
            ${(po.paidStatus || "Unpaid").toUpperCase()}
          </span>
        </div>
      </div>
    </div>

    <table class="items-table">
      <thead>
        <tr>
          <th style="width: 35px;" class="text-center">#</th>
          <th>Description</th>
          <th style="width: 70px;" class="text-center">Qty</th>
          <th style="width: 120px;" class="text-right">Unit Price</th>
          <th style="width: 130px;" class="text-right">Line Total</th>
        </tr>
      </thead>
      <tbody>
        ${items.map(item => `
          <tr>
            <td class="text-center" style="color: #94a3b8;">${item.index}</td>
            <td><strong>${item.description}</strong></td>
            <td class="text-center">${item.quantity}</td>
            <td class="text-right">${fmt(item.unitPrice)}</td>
            <td class="text-right" style="font-weight: 600;">${fmt(item.total)}</td>
          </tr>
        `).join("")}
      </tbody>
    </table>

    <div class="summary-section">
      <div class="totals-box">
        <div class="totals-row">
          <span>Subtotal:</span>
          <span>${fmt(subtotal)}</span>
        </div>
        <div class="totals-row grand-total">
          <span>Total Billed:</span>
          <span>${fmt(totalAmount)}</span>
        </div>
        ${paidAmount > 0 ? `
        <div class="totals-row" style="color: #16a34a;">
          <span>Amount Paid:</span>
          <span>-${fmt(paidAmount)}</span>
        </div>` : ""}
        <div class="totals-row balance-due">
          <span>Balance Due:</span>
          <span>${fmt(balanceDue)}</span>
        </div>
      </div>
    </div>

    <div class="footer-notes">
      <p>Thank you for your business! Please make payments in accordance with the specified terms.</p>
      <p style="margin-top: 4px;">If you have any questions concerning this invoice, please contact our accounting department.</p>
    </div>
  </div>

  `;

  openDocumentPrintWindow({
    title: `Invoice - ${po.poNumber || "Order"}`,
    headStyles: styles,
    bodyHtml
  });
}

/**
 * Formats and triggers print/PDF export for an order's material cost breakdown.
 */
export function exportMaterialCostToPdf(po, options = {}) {
  if (!po) return;

  const { logoUrl, companyName } = options;

  const fmt = (val) => {
    const num = Number(val || 0);
    return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const rows = [];
  (po.items || []).forEach((item, itemIdx) => {
    if (item.vendors && item.vendors.length > 0) {
      item.vendors.forEach((v, vIdx) => {
        const vendorKey = `${item.id || itemIdx}_${v.id || vIdx}`;
        const orderedDate = (po.itemStatuses && po.itemStatuses[vendorKey]?.orderedDate)
          || (po.itemStatuses && po.itemStatuses[item.id]?.orderedDate)
          || v.orderedDate
          || item.orderedDate
          || (v.ordered ? (po.orderedDate || "Purchased") : "Not Purchased");

        const qty = Number(v.quantity || item.quantity || 1);
        const unitCost = Number(v.vendorPrice !== undefined ? v.vendorPrice : (item.vendorPrice || 0));
        const taxRate = Math.max(0, Number(v.tax !== undefined ? v.tax : (item.tax || 0)));
        const costWithTax = (qty * unitCost) * (1 + taxRate / 100);
        const shippingCost = Number(v.shippingCost || 0);
        const lineVendorCost = costWithTax + shippingCost;

        rows.push({
          vendorName: v.vendor || item.vendor || "—",
          description: item.description || "Line Item",
          quantity: qty,
          unitCost: unitCost,
          vendorCost: lineVendorCost,
          paymentMethod: v.paymentMethod || item.paymentMethod || "—",
          datePurchased: orderedDate
        });
      });
    } else {
      const itemKey = item.id || `${itemIdx}`;
      const orderedDate = (po.itemStatuses && po.itemStatuses[itemKey]?.orderedDate)
        || item.orderedDate
        || (item.ordered ? (po.orderedDate || "Purchased") : "Not Purchased");

      const qty = Number(item.quantity || 1);
      const unitCost = Number(item.vendorPrice || item.vendorCost || 0);
      const taxRate = Math.max(0, Number(item.tax || 0));
      const costWithTax = (qty * unitCost) * (1 + taxRate / 100);
      const shippingCost = Number(item.shippingCost || 0);
      const lineVendorCost = costWithTax + shippingCost;

      rows.push({
        vendorName: item.vendor || "—",
        description: item.description || "Line Item",
        quantity: qty,
        unitCost: unitCost,
        vendorCost: lineVendorCost,
        paymentMethod: item.paymentMethod || "—",
        datePurchased: orderedDate
      });
    }
  });

  const totalMaterialCost = rows.reduce((sum, r) => sum + r.vendorCost, 0);

  const quoteSubmittedDate = po.submittedDate || po.quoteSubmittedDate || po.dateSubmitted || po.createdAt || po.date || "N/A";
  const poWonDate = po.awardedDate || po.wonDate || po.poWonDate || "N/A";

  const styles = `
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      color: #1e293b;
      background: #ffffff;
      padding: 40px;
      font-size: 13px;
      line-height: 1.5;
    }
    .report-container {
      max-width: 850px;
      margin: 0 auto;
    }
    .header-row {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      border-bottom: 2px solid #0f766e;
      padding-bottom: 20px;
      margin-bottom: 24px;
    }
    .brand-section {
      display: flex;
      align-items: center;
      gap: 16px;
    }
    .logo-img {
      width: 60px;
      height: 60px;
      border-radius: 8px;
      object-fit: cover;
    }
    .company-title {
      font-size: 22px;
      font-weight: 800;
      color: #0f172a;
      letter-spacing: -0.5px;
    }
    .company-sub {
      font-size: 12px;
      color: #64748b;
    }
    .badge-block {
      text-align: right;
    }
    .doc-type {
      font-size: 22px;
      font-weight: 900;
      color: #0f766e;
      letter-spacing: 0.5px;
      text-transform: uppercase;
    }
    .po-num {
      font-size: 14px;
      font-weight: 700;
      color: #334155;
      margin-top: 4px;
    }
    .meta-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 20px;
      margin-bottom: 24px;
    }
    .card-box {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      padding: 14px 18px;
    }
    .card-label {
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #0f766e;
      margin-bottom: 8px;
    }
    .client-name {
      font-size: 16px;
      font-weight: 700;
      color: #0f172a;
      margin-bottom: 4px;
    }
    .meta-line {
      display: flex;
      justify-content: space-between;
      padding: 3px 0;
      font-size: 12px;
    }
    .meta-line .lbl { color: #64748b; font-weight: 500; }
    .meta-line .val { color: #0f172a; font-weight: 600; }
    
    .cost-highlight-box {
      background: #f0fdf4;
      border: 1px solid #86efac;
      border-radius: 6px;
      padding: 14px 18px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
    }
    .cost-highlight-label {
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      color: #166534;
      letter-spacing: 0.5px;
    }
    .cost-highlight-val {
      font-size: 22px;
      font-weight: 800;
      color: #15803d;
    }

    table.items-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 24px;
    }
    table.items-table th {
      background-color: #0f172a;
      color: #ffffff;
      text-align: left;
      padding: 10px 12px;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      font-weight: 600;
    }
    table.items-table th.text-right { text-align: right; }
    table.items-table th.text-center { text-align: center; }
    table.items-table td {
      padding: 10px 12px;
      border-bottom: 1px solid #e2e8f0;
      color: #334155;
      font-size: 12px;
    }
    table.items-table tr:nth-child(even) td {
      background-color: #f8fafc;
    }

    .summary-section {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 28px;
    }
    .totals-box {
      width: 300px;
      background: #f8fafc;
      border: 1px solid #cbd5e1;
      border-radius: 6px;
      padding: 14px 18px;
    }
    .totals-row {
      display: flex;
      justify-content: space-between;
      padding: 4px 0;
      font-size: 12.5px;
      color: #475569;
    }
    .totals-row.grand-total {
      border-top: 2px solid #0f766e;
      margin-top: 8px;
      padding-top: 8px;
      font-size: 15px;
      font-weight: 800;
      color: #0f172a;
    }
    .footer-notes {
      border-top: 1px solid #e2e8f0;
      padding-top: 16px;
      font-size: 11px;
      color: #64748b;
      text-align: center;
    }
    .print-actions {
      margin-bottom: 20px;
      text-align: right;
    }
    .print-btn {
      background: #0f766e;
      color: #ffffff;
      border: none;
      padding: 8px 18px;
      border-radius: 6px;
      font-weight: 600;
      font-size: 13px;
      cursor: pointer;
    }
    .print-btn:hover {
      background: #115e59;
    }

    @media print {
      body { padding: 0; }
      .print-actions { display: none; }
      @page { size: auto; margin: 12mm; }
    }
  `;

  const bodyHtml = `
  <div class="report-container">

    <div class="header-row">
      <div class="brand-section">
        ${logoUrl ? `<img src="${logoUrl}" class="logo-img" alt="Logo" />` : ""}
        <div>
          <div class="company-title">${companyName || "Jimini Logistics"}</div>
          <div class="company-sub">Enterprise Logistics & Procurement Services</div>
        </div>
      </div>
      <div class="badge-block">
        <div class="doc-type">Material Cost Report</div>
        <div class="po-num">PO # ${po.poNumber || "N/A"}</div>
      </div>
    </div>

    <div class="cost-highlight-box">
      <div>
        <div class="cost-highlight-label">Total Material Cost</div>
        <div style="font-size: 11.5px; color: #4b5563;">Combined direct vendor purchases & line costs</div>
      </div>
      <div class="cost-highlight-val">${fmt(totalMaterialCost)}</div>
    </div>

    <div class="meta-grid">
      <div class="card-box">
        <div class="card-label">Order & Customer</div>
        <div class="client-name">${po.clientName || "Valued Client"}</div>
        <div class="meta-line">
          <span class="lbl">Customer PO #:</span>
          <span class="val">${po.customerPoNumber || "N/A"}</span>
        </div>
        <div class="meta-line">
          <span class="lbl">PO Number:</span>
          <span class="val">${po.poNumber || "N/A"}</span>
        </div>
      </div>

      <div class="card-box">
        <div class="card-label">Timeline & Dates</div>
        <div class="meta-line">
          <span class="lbl">Quote Submitted Date:</span>
          <span class="val">${quoteSubmittedDate}</span>
        </div>
        <div class="meta-line">
          <span class="lbl">PO Won Date:</span>
          <span class="val">${poWonDate}</span>
        </div>
        <div class="meta-line">
          <span class="lbl">Export Date:</span>
          <span class="val">${new Date().toISOString().split("T")[0]}</span>
        </div>
      </div>
    </div>

    <table class="items-table">
      <thead>
        <tr>
          <th style="width: 140px;">Vendor Name</th>
          <th>Item / Description</th>
          <th style="width: 120px;">Payment Method</th>
          <th style="width: 110px;" class="text-right">Vendor Cost</th>
          <th style="width: 110px;" class="text-center">Date Purchased</th>
        </tr>
      </thead>
      <tbody>
        ${rows.length === 0 ? `
          <tr>
            <td colspan="5" style="text-align: center; color: #94a3b8; padding: 20px;">No line items found for this PO.</td>
          </tr>
        ` : rows.map(r => `
          <tr>
            <td><strong>${r.vendorName}</strong></td>
            <td>
              <div>${r.description}</div>
              <div style="font-size: 11px; color: #64748b;">Qty: ${r.quantity} &times; ${fmt(r.unitCost)}</div>
            </td>
            <td><span style="display: inline-block; padding: 2px 8px; background: #e2e8f0; border-radius: 4px; font-size: 11px; font-weight: 600;">${r.paymentMethod}</span></td>
            <td class="text-right" style="font-weight: 700; color: #0f172a;">${fmt(r.vendorCost)}</td>
            <td class="text-center">${r.datePurchased}</td>
          </tr>
        `).join("")}
      </tbody>
    </table>

    <div class="summary-section">
      <div class="totals-box">
        <div class="totals-row">
          <span>Total Line Items:</span>
          <span>${rows.length}</span>
        </div>
        <div class="totals-row grand-total">
          <span>Total Material Cost:</span>
          <span>${fmt(totalMaterialCost)}</span>
        </div>
      </div>
    </div>

    <div class="footer-notes">
      <p>Internal Procurement & Material Cost Breakdown &bull; Jimini Logistics</p>
    </div>
  </div>

  `;

  openDocumentPrintWindow({
    title: `Material Cost - ${po.poNumber || "Order"}`,
    headStyles: styles,
    bodyHtml
  });
}
